/**
 * Wires the in-app sync queue (`createSyncQueue`) into the zustand sync slice.
 *
 * Without this module, every `useStore.getState().sync.enqueue(...)` call
 * would only update the *local* `pending[]` list and **never reach Google
 * Sheets** — the most common dogfood bug ("everything I add disappears on
 * refresh").
 *
 * Lifecycle:
 *   - `initSyncWiring({ client, spreadsheetId })` builds the singleton queue
 *     with handlers for every `SyncOpType`. Call once after sheets bootstrap
 *     completes.
 *   - `enqueueOp(op)` is the canonical write path: it both (a) records into
 *     the zustand slice for UI subscribers (待同步 badges, CloudSyncScreen)
 *     AND (b) hands the op to the real queue, which writes to localStorage,
 *     spawns the worker, calls the right repo method, and re-syncs the
 *     slice via the queue's `onChange` snapshot hook.
 *   - `tearDownSyncWiring()` stops the worker on logout.
 *
 * Sharp edges:
 *   - The slice's `enqueue` action is INTENTIONALLY left as a pure store
 *     mutation (the queue's `onChange` is the single source of truth for
 *     pending/failed lists). Callers MUST go through `enqueueOp` instead.
 *   - On OCC conflict (rev mismatch in `update_*` ops) the queue swallows
 *     the error and writes a `notifications` row — UI surfaces it via the
 *     bell badge.
 *   - Migration ops (`runMigrations`) are NOT enqueued — they run inline
 *     during bootstrap before the queue is ready.
 */

import { useStore } from '@/state/store';
import {
  createSyncQueue,
  type SyncQueue,
  type SyncOpInput,
  type SyncOpHandler,
  type SyncOpType,
} from './sync-queue';
import type { SheetsClient } from './client';
import { transactionsRepo } from './repositories/transactions';
import { booksRepo } from './repositories/books';
import { categoriesRepo } from './repositories/categories';
import { recurringRepo } from './repositories/recurring';
import { notificationsRepo } from './repositories/notifications';
import { metaRepo } from './repositories/meta';
import type { MetaKey } from './types';

let queue: SyncQueue | null = null;

interface InitArgs {
  client: SheetsClient;
  spreadsheetId: string;
}

function buildHandlers({ client, spreadsheetId }: InitArgs): Partial<Record<SyncOpType, SyncOpHandler>> {
  const ctx = { client, spreadsheetId };
  return {
    create_tx: async (payload) => {
      const p = payload as {
        id: string;
        book_id: string;
        date: string;
        amount_minor: number;
        currency: string;
        kind: 'expense' | 'income' | 'transfer';
        category_id: string;
        subcategory_id: string;
        note: string;
        project: string;
        tags: string;
        recurring_id: string;
        time?: string;
      };
      // Backwards compat: v4 queued ops carried `time` separately; v5
      // schema folds it into `date`. If we see a legacy entry, merge
      // before the write so the resulting sheet row matches v5 layout.
      const date = p.time && p.time.length > 0
        ? `${p.date}T${p.time.length >= 5 ? p.time.slice(0, 5) : p.time}`
        : p.date;
      const { time: _legacyTime, ...rest } = p;
      void _legacyTime;
      await transactionsRepo.create(client, spreadsheetId, { ...rest, date });
    },
    update_tx: async (payload) => {
      const p = payload as { id: string; rev: number; patch: Record<string, unknown> };
      await transactionsRepo.update(client, spreadsheetId, p.id, p.patch, p.rev);
    },
    delete_tx: async (payload) => {
      const p = payload as { id: string; rev: number };
      await transactionsRepo.softDelete(client, spreadsheetId, p.id, p.rev);
    },

    create_book: async (payload) => {
      const p = payload as Parameters<typeof booksRepo.create>[2];
      await booksRepo.create(client, spreadsheetId, p);
    },
    update_book: async (payload) => {
      const p = payload as { id: string; rev: number; patch: Record<string, unknown> };
      await booksRepo.update(client, spreadsheetId, p.id, p.patch, p.rev);
    },
    delete_book: async (payload) => {
      const p = payload as { id: string; rev: number };
      await booksRepo.softDelete(client, spreadsheetId, p.id, p.rev);
    },

    create_category: async (payload) => {
      const p = payload as Parameters<typeof categoriesRepo.create>[2];
      await categoriesRepo.create(client, spreadsheetId, p);
    },
    update_category: async (payload) => {
      const p = payload as { id: string; rev: number; patch: Record<string, unknown> };
      await categoriesRepo.update(client, spreadsheetId, p.id, p.patch, p.rev);
    },
    delete_category: async (payload) => {
      const p = payload as { id: string; rev: number };
      await categoriesRepo.softDelete(client, spreadsheetId, p.id, p.rev);
    },

    create_recurring: async (payload) => {
      const p = payload as Parameters<typeof recurringRepo.create>[2];
      await recurringRepo.create(client, spreadsheetId, p);
    },
    update_recurring: async (payload) => {
      const p = payload as { id: string; rev: number; patch: Record<string, unknown> };
      await recurringRepo.update(client, spreadsheetId, p.id, p.patch, p.rev);
    },

    set_meta: async (payload) => {
      const p = payload as { key: MetaKey; value: string };
      await metaRepo.setValue(ctx, p.key, p.value);
    },

    create_notification: async (payload) => {
      const p = payload as Parameters<typeof notificationsRepo.append>[1];
      await notificationsRepo.append(ctx, p);
    },
    update_notification: async (payload) => {
      const p = payload as { id: string; read_at: string };
      await notificationsRepo.markRead(ctx, p.id);
      // markRead currently only flips `read_at` to now() — if the slice asked
      // for a specific ISO we accept that and let the local optimistic stay
      // (Sheet just gets the timestamp the repo writes).
      void p.read_at;
    },
  };
}

/**
 * Build the singleton queue and hook its snapshot into the sync slice.
 * Safe to call multiple times — earlier instances are torn down first.
 */
export function initSyncWiring(args: InitArgs): void {
  tearDownSyncWiring();

  queue = createSyncQueue({
    spreadsheetId: args.spreadsheetId,
    client: args.client,
    handlers: buildHandlers(args),
    onChange: (snap) => {
      // Mirror queue state into the slice so UI subscribers (CloudSync, the
      // 待同步 badge in RecentTxList) reflect reality. The slice's own pure
      // setter is bypassed — we set state directly.
      useStore.setState((s) => ({
        ...s,
        sync: {
          ...s.sync,
          pending: snap.pending as typeof s.sync.pending,
          lastSyncAt: snap.lastSyncAt,
        },
      }));
    },
  });
}

export function tearDownSyncWiring(): void {
  if (queue) {
    queue.clear();
    queue = null;
  }
  // Also clear the slice so logout doesn't leave a stale queue snapshot.
  const s = useStore.getState();
  s.sync.reset();
}

/**
 * The canonical enqueue path — every UI write MUST go through this.
 * Falls back to a console warning when wiring isn't initialised yet
 * (happens if a write fires before bootstrap completes; the op is dropped
 * to avoid silent corruption).
 */
export function enqueueOp(op: SyncOpInput): void {
  if (!queue) {
    // eslint-disable-next-line no-console
    console.error('[Mochia] enqueueOp called before initSyncWiring; op dropped', op);
    return;
  }
  queue.enqueue(op);
}

/** Force-flush — used by Settings → 雲端同步 → 立即同步. */
export async function drainSyncQueue(): Promise<void> {
  if (!queue) return;
  await queue.drain();
}
